import { useState, useEffect, useCallback, useMemo } from 'react';
import { employeeService } from 'api/services/employeeService';
import type {
  ID, PaginatedResponse, Employee,
  CreateEmployeeRequest, UpdateEmployeeRequest, EmployeeSearchRequest,
  TerminationPreview, TerminateEmployeeRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useEmployees = (params?: EmployeeSearchRequest) => {
  const [data, setData] = useState<Employee[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Employee>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchEmployees = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await employeeService.getAll(params);
      setData(response.data); setPagination(response.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  return { data, pagination, loading, error, refetch: fetchEmployees };
};

export const useEmployee = (id: ID | null | undefined) => {
  const [data, setData] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchEmployee = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const response = await employeeService.getById(id);
      setData(response.data);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchEmployee(); }, [fetchEmployee]);
  return { data, loading, error, refetch: fetchEmployee };
};

export const useCreateEmployee = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createEmployee = async (data: CreateEmployeeRequest, options?: { onSuccess?: (item: Employee) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try {
      const response = await employeeService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createEmployee, loading, error };
};

export const useUpdateEmployee = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateEmployee = async (id: ID, data: UpdateEmployeeRequest, options?: { onSuccess?: (item: Employee) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try {
      const response = await employeeService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateEmployee, loading, error };
};

export const useDeleteEmployee = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteEmployee = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try {
      await employeeService.delete(id);
      options?.onSuccess?.();
    } catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteEmployee, loading, error };
};

// =============================================================================
//  Termination
// =============================================================================

/**
 * Lazy preview hook — does NOT auto-fetch. The terminate modal calls
 * `fetchPreview` with the candidate dateLeft after the user picks a date so
 * we don't fire a request on every keystroke.
 */
export const useTerminationPreview = () => {
  const [data, setData] = useState<TerminationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPreview = useCallback(async (id: ID, dateLeft: string) => {
    setLoading(true); setError(null);
    try {
      const response = await employeeService.getTerminationPreview(id, dateLeft);
      setData(response.data);
      return response.data;
    } catch (err) { const e = err as Error; setError(e); throw e; }
    finally { setLoading(false); }
  }, []);

  const reset = useCallback(() => { setData(null); setError(null); }, []);

  return { data, loading, error, fetchPreview, reset };
};

export const useTerminateEmployee = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const terminate = async (
    id: ID,
    data: TerminateEmployeeRequest,
    options?: { onSuccess?: (item: Employee) => void; onError?: (e: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await employeeService.terminate(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { terminate, loading, error };
};
